/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from "react";

import { getRailsClient } from "../../../client";
import { SiteSettings, SettingsContextType, SettingsContextProviderProps } from "./settings.types";

const SiteSettingsApiRoute = "/api/internal_api/v2/site";

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsContextProvider: React.FC<SettingsContextProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getRailsClient({ useToast: true }).get(SiteSettingsApiRoute);

      if (response.status === 200) {
        setSettings(response.data?.site);
      } else {
        throw new Error(`Unable to get site settings, status code ${response.status}`);
      }
    } catch (err) {
      console.error("Failed to fetch site settings:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch site settings"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const value: SettingsContextType = {
    settings,
    settingsLoading: isLoading,
    error,
    refetchSettings: fetchSettings,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettingsContext = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettingsContext must be used within a SettingsContextProvider");
  }
  return context;
};
